import { useEffect, useMemo, useState } from 'react';
import { AxiosError } from 'axios';
import { api, CvSkill, errorMessage } from '../api';
import Footer from '../Components/Footer';
import { Alert, Loading, useAction, useLoad } from '../Components/ui';

/** Reads the file name from Content-Disposition (filename* carries the å/ä/ö). */
function downloadName(header: string | undefined, fallback: string): string {
    const encoded = header?.match(/filename\*=UTF-8''([^;]+)/i)?.[1];
    return encoded ? decodeURIComponent(encoded) : fallback;
}

/** With responseType 'blob' the JSON error body arrives as a Blob too. */
async function blobErrorMessage(err: unknown): Promise<string> {
    if (err instanceof AxiosError && err.response?.data instanceof Blob) {
        try {
            const data = JSON.parse(await err.response.data.text()) as { error?: string; fields?: Array<{ message: string }> };
            if (data.fields?.length) return data.fields.map(f => f.message).join('. ');
            if (data.error) return data.error;
        } catch { /* not JSON */ }
    }
    return errorMessage(err);
}

/** /cv — pick the skills for the Skills section of the CV and download it as a PDF. */
export default function CvGenerator() {
    const { data, error: loadError, loading } = useLoad(
        () => api.get<{ skills: CvSkill[]; defaults: string[] }>('/cv/skills').then(r => r.data), []);
    const [selected, setSelected] = useState<string[]>([]);
    const [category, setCategory] = useState('');
    const [search, setSearch] = useState('');
    const [expanded, setExpanded] = useState<string | null>(null);
    const { busy, error, setError, run } = useAction();

    useEffect(() => { if (data) setSelected(data.defaults); }, [data]);

    const byId = useMemo(() => new Map(data?.skills.map(s => [s.id, s])), [data]);
    const categories = useMemo(() => [...new Set(data?.skills.map(s => s.category))], [data]);
    const available = (data?.skills ?? []).filter(s =>
        !selected.includes(s.id)
        && (!category || s.category === category)
        && s.name.toLowerCase().includes(search.trim().toLowerCase()));

    const add = (id: string) => setSelected(list => [...list, id]);
    const remove = (id: string) => setSelected(list => list.filter(s => s !== id));
    const move = (index: number, by: number) => setSelected(list => {
        const next = [...list];
        [next[index], next[index + by]] = [next[index + by], next[index]];
        return next;
    });

    const generate = () => void run(async () => {
        try {
            const res = await api.post<Blob>('/cv/generate', { skills: selected }, { responseType: 'blob' });
            const url = URL.createObjectURL(res.data);
            const a = document.createElement('a');
            a.href = url;
            a.download = downloadName(res.headers['content-disposition'] as string | undefined, 'CV.pdf');
            a.click();
            URL.revokeObjectURL(url);
        } catch (err) {
            setError(await blobErrorMessage(err));
        }
    });

    return (
        <>
            <section className="section page-section">
                <span className="section-label">Personal</span>
                <h1 className="page-title">CV generator</h1>
                <p className="section-desc">Choose the skills for the Skills section, put them in order and export the CV as a PDF.</p>

                {loadError && <Alert type="error">{loadError}</Alert>}
                {loading && !data ? <Loading /> : data && (
                    <div className="card-grid cols-2 cv-generator">
                        <div className="panel-card">
                            <div className="panel-card-header">
                                <h3>Available skills</h3>
                            </div>
                            <div className="fi-form cv-filters">
                                <input type="search" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search skills" />
                                <select value={category} onChange={e => setCategory(e.target.value)}>
                                    <option value="">All categories</option>
                                    {categories.map(c => <option key={c} value={c}>{c}</option>)}
                                </select>
                            </div>
                            <ul className="cv-skill-list">
                                {available.map(s => (
                                    <li key={s.id}>
                                        <div className="cv-skill-row">
                                            <button className="link-button cv-skill-name" onClick={() => setExpanded(expanded === s.id ? null : s.id)}>
                                                {s.name} <span className="tag tag-category">{s.category}</span>
                                            </button>
                                            <button className="btn-outline small" onClick={() => add(s.id)}>Add</button>
                                        </div>
                                        {expanded === s.id && s.paragraphs.map((p, i) => <p key={i} className="card-text cv-skill-text">{p}</p>)}
                                    </li>
                                ))}
                                {available.length === 0 && <li className="empty-state">No more skills match.</li>}
                            </ul>
                        </div>

                        <div className="panel-card">
                            <div className="panel-card-header">
                                <h3>In the CV ({selected.length})</h3>
                                <button className="btn-outline small" onClick={() => setSelected(data.defaults)}>Reset to original</button>
                            </div>
                            <ol className="cv-skill-list">
                                {selected.map((id, i) => {
                                    const s = byId.get(id);
                                    if (!s) return null;
                                    return (
                                        <li key={id}>
                                            <div className="cv-skill-row">
                                                <button className="link-button cv-skill-name" onClick={() => setExpanded(expanded === id ? null : id)}>
                                                    {i + 1}. {s.name}
                                                </button>
                                                <div className="btn-row">
                                                    <button className="btn-outline small" disabled={i === 0} onClick={() => move(i, -1)} aria-label="Move up">↑</button>
                                                    <button className="btn-outline small" disabled={i === selected.length - 1} onClick={() => move(i, 1)} aria-label="Move down">↓</button>
                                                    <button className="btn-outline small danger" onClick={() => remove(id)}>Remove</button>
                                                </div>
                                            </div>
                                            {expanded === id && s.paragraphs.map((p, j) => <p key={j} className="card-text cv-skill-text">{p}</p>)}
                                        </li>
                                    );
                                })}
                                {selected.length === 0 && <li className="empty-state">Add at least one skill.</li>}
                            </ol>
                            {error && <Alert type="error">{error}</Alert>}
                            <button className="btn-submit" disabled={busy || selected.length === 0} onClick={generate}>
                                {busy ? 'Generating…' : 'Generate & download PDF'}
                            </button>
                        </div>
                    </div>
                )}
            </section>
            <Footer />
        </>
    );
}
